import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  Subject,
  Subscription,
  delay,
  finalize,
  of,
  takeUntil,
  tap,
} from 'rxjs';
import { ShareDirectivesModule } from 'src/app/core/directives/directives.module';
import { NumberSeparatorPipe } from 'src/app/core/pipes/number.separator.pipe';

import { SharePipesModule } from 'src/app/core/pipes/pipes.module';
import {
  DialogService,
  DialogStatus,
} from 'src/app/core/services/dialog.service';
import { FileService } from 'src/app/core/services/file.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { AppMobilesAnalysisSelectedMode } from 'src/app/core/types/app-mobiles-analysis-selected-mode.type';
import { TranslationModule } from 'src/app/i18n';
import { MaterialModule } from 'src/app/shared/modules/material/material.module';

@Component({
  selector: 'cells-widget',
  templateUrl: './cells-widget.component.html',
  styleUrls: ['./cells-widget.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    NgbModule,
    FormsModule,
    ShareDirectivesModule,
    ReactiveFormsModule,
    TranslationModule,
    SharePipesModule,
  ],
  providers: [NumberSeparatorPipe],
})
export class CellsWidgetComponent {
  //#region properties
  @Input() cellList: Array<number>;
  @Input('uniqId') input_id: string;
  @Input('form') input_form: FormGroup;
  @Input('controlName') input_formControlName: string;

  pastedLoading: boolean = false;
  protected onDestroy$ = new Subject<void>();
  pastedSubscription: Subscription;
  names: string = '';
  namesCount: number = 0;
  maxPastedTextLength: number = 1000000;
  maxPastedTextLengthInManualMode: number = 100;
  selectedMode: AppMobilesAnalysisSelectedMode = 'manual';
  namesFile: string = '';

  @Output('names') outputModel = new EventEmitter<string>();
  @Output('selectedMode') outputSelectedMode =
    new EventEmitter<AppMobilesAnalysisSelectedMode>();
  //#endregion

  constructor(
    protected dialogService: DialogService,
    protected translate: TranslateService,
    protected UtilityService: UtilityService,
    protected fileService: FileService,
    protected renderer: Renderer2,
    protected sanitizer: DomSanitizer,
    protected numberSeparatorPipe: NumberSeparatorPipe
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cellList']) this.createNamesFile();
  }

  onChangeMode(mode: AppMobilesAnalysisSelectedMode) {
    this.names = null;
    this.namesCount = 0;
    this.namesFile = null;
    this.selectedMode = mode;
    this.outputSelectedMode.emit(this.selectedMode);
  }

  //#region Checking Methods

  checkInputText(event) {
    // this.names = null;

    let inputText = (event.target as HTMLInputElement).value;

    if (event) if (event?.key !== 'Enter' && event?.type != 'change') return;

    this.setFinalModel(inputText);
  }

  checkPastedText(event, inputPastedText?: any) {
    this.names = null;
    if (inputPastedText) {
      //چک میکنه قبلا شماره ای وارد شده
      if (this.names) {
        inputPastedText =
          this.names.replace(/,/g, '\n') + '\n' + inputPastedText;
        this.clearNames();
      }
      this.setFinalModel(inputPastedText);

      return;
    }

    const clipboardTextareaElement = document.getElementById(
      this.input_id + '-clipboard-textarea'
    ) as HTMLTextAreaElement;

    event?.preventDefault();

    // آنساب اسکرایب می کنه قبلی ها را برای هدر نرفتن سرعت و منابع
    if (this.pastedSubscription) this.pastedSubscription.unsubscribe();

    //دادها ها را بصورت آبزرویبل میگیره
    of(event.clipboardData.getData('text'))
      .pipe(
        tap(() => (this.pastedLoading = true)),
        takeUntil(this.onDestroy$),
        delay(200), //delay
        finalize(() => (this.pastedLoading = false))
      )
      .subscribe((pastedText) => {
        this.pastedLoading = false;
        const timeOut = setTimeout(() => {
          this.renderer.setProperty(clipboardTextareaElement, 'value', '');

          // clipboardTextareaElement.value = '';
          clearTimeout(timeOut);
        }, 200);

        //چک میکنه قبلا شماره ای وارد شده
        if (this.selectedMode == 'clipboard' && this.names) {
          pastedText = this.names.replace(/,/g, '\n') + '\n' + pastedText;

          this.clearNames();
        } else if (this.selectedMode == 'manual' && this.names) {
          pastedText = this.names.replace(/,/g, '\n') + '\n' + pastedText;
          this.clearNames();
        }
        if (pastedText) {
          pastedText = this.UtilityService.getTextWithEnglishNumber(pastedText);
          this.setFinalModel(pastedText);
        }
      });
  }

  checkTextSizeIsValid(maxTextSize): boolean {
    if (this.namesCount > maxTextSize) {
      let message = '';

      message = this.translate.instant(
        'messages.cellNamesMaxLengthInManualMode',
        {
          size: this.numberSeparatorPipe.transform(maxTextSize),
        }
      );

      this.dialogService.openDialog({
        title: 'common.error',
        message: message,
        status: DialogStatus.error,
      });
      return false;
    }
    return true;
  }

  setFinalModel(data: any) {
    let result: string[] = [];
    //پاک کردن خروجی
    if (!data) {
      if (this.selectedMode == 'manual') this.names = '';
      else if (this.selectedMode == 'clipboard') this.names = '';
      this.emit();
      return;
    }
    if (typeof data === 'string') {
      result = data.splitByNewLine();
      this.namesCount = result?.length;
      if (
        !this.checkTextSizeIsValid(
          this.selectedMode == 'manual'
            ? this.maxPastedTextLengthInManualMode
            : this.maxPastedTextLength
        )
      )
        return;
      if (this.selectedMode == 'manual') this.names = result.join('');
      else if (this.selectedMode == 'clipboard') this.names = result.join('');

      this.emit();
    } else result = data;

    if (this.selectedMode == 'manual') this.names = result.join('');
    else if (this.selectedMode == 'clipboard') this.names = result.join('');

    this.emit();
  }

  //#endregion

  clearNames() {
    this.names = '';
    this.namesCount = 0;
    this.namesFile = null;
    this.emit();
  }

  copyToClipboard(text) {
    navigator.clipboard.writeText(text);
  }

  getDataFromClipboard() {
    navigator.clipboard
      .readText()
      .then((text) => {
        this.checkPastedText(null, text);
      })
      .catch((err) => {});
  }

  createNamesFile() {
    this.namesFile = this.createTextFile(
      '[' + this.cellList.join().replace(/,/g, ',\n') + ']'
    );
  }

  createTextFile(data): any {
    const blob = new Blob([data], { type: 'application/octet-stream' });
    let fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      window.URL.createObjectURL(blob)
    );
    return fileUrl;
  }

  emit() {
    this.names = this.names.replace(/\n/g, ',');

    let namesArray: string[] = this.names.split(',');

    this.names = namesArray?.join(',');

    this.outputModel.emit(this.names);
  }
}
